import React from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  PlaylistAddCheck as TasksIcon,
  CalendarMonth as CalendarIcon,
  PermMedia as PermMediaIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { JOBS_ROLES, SCHEDULING_ROLES, SCHEDULING_ADMIN_ONLY, ALL_ADMIN_ROLES } from '../auth/roles';
import { AssignmentInd as AssignmentIcon } from '@mui/icons-material';

const drawerWidthExpanded = 240;
const drawerWidthCollapsed = 64;

interface LayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  pageActions?: React.ReactNode;
}

const menuItems = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/',
    roles: JOBS_ROLES,
  },
  {
    text: 'Jobs',
    icon: <WorkIcon />,
    path: '/jobs',
    roles: JOBS_ROLES,
  },
  {
    text: 'Scheduling',
    icon: <CalendarIcon />,
    path: '/scheduling',
    roles: SCHEDULING_ROLES,
  },
  {
    text: 'Reporters',
    icon: <PeopleIcon />,
    path: '/reporters',
    roles: SCHEDULING_ROLES,
  },
  {
    text: 'Assigners',
    icon: <AssignmentIcon />,
    path: '/assigners',
    roles: SCHEDULING_ADMIN_ONLY,
  },
  {
    text: 'Reporter Resources',
    icon: <PermMediaIcon />,
    path: '/resources',
    roles: ALL_ADMIN_ROLES,
  },
  {
    text: 'Background Tasks',
    icon: <TasksIcon />,
    path: '/tasks',
    roles: JOBS_ROLES,
  },
  {
    text: 'Settings',
    icon: <SettingsIcon />,
    path: '/settings',
    roles: ALL_ADMIN_ROLES,
  },
];

export const Layout: React.FC<LayoutProps> = ({ children, pageTitle, pageActions }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => localStorage.getItem('nav-collapsed') === 'true');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout, user, hasAnyRole } = useAuth();
  const drawerWidth = collapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  const visibleMenuItems = menuItems.filter(item =>
    !item.roles || hasAnyRole(item.roles as string[])
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawer = (
    <div>
      <Toolbar sx={{
        display: 'flex',
        flexDirection: collapsed ? 'column' : 'column',
        alignItems: 'center',
        py: 2,
        minHeight: collapsed ? 56 : undefined,
        position: 'relative',
      }}>
        {collapsed ? (
          <Box component="img"
               src="/favicon-194x194.png"
               alt="Magna Legal Services"
               sx={{ height: 32, width: 32 }} />
        ) : (
          <>
            <Box component="img"
                 src="/magna-logo-official-dark.svg"
                 alt="Magna Legal Services"
                 sx={{ height: 32, mb: 1 }} />
            <Typography variant="subtitle1" noWrap component="div" sx={{
              color: '#3A506B',
              fontWeight: 600,
              textAlign: 'center'
            }}>
              Reporter Portal
            </Typography>
            <Typography variant="caption" sx={{
              color: '#708181',
              textAlign: 'center'
            }}>
              Admin
            </Typography>
            {user && (
              <Typography variant="caption" sx={{
                color: '#708181',
                textAlign: 'center',
                mt: 1
              }}>
                {user.name || user.username}
              </Typography>
            )}
          </>
        )}
      </Toolbar>
      <Divider />
      <List>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={collapsed ? item.text : ''} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{ justifyContent: collapsed ? 'center' : 'initial', px: collapsed ? 1 : 2 }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? 'Logout' : ''} placement="right">
            <ListItemButton
              onClick={handleLogout}
              sx={{ justifyContent: collapsed ? 'center' : 'initial', px: collapsed ? 1 : 2 }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                <LogoutIcon />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Logout" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        {!isMobile && (
          <ListItem disablePadding>
            <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
              <ListItemButton
                onClick={() => setCollapsed(prev => { const next = !prev; localStorage.setItem('nav-collapsed', String(next)); return next; })}
                sx={{ justifyContent: collapsed ? 'center' : 'initial', px: collapsed ? 1 : 2 }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                  {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Collapse" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          transition: 'width 0.2s ease-in-out, margin-left 0.2s ease-in-out',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ 
            color: '#3A506B',
            fontWeight: 600,
            flexGrow: pageActions ? 1 : 0
          }}>
            {pageTitle || 'Admin Portal'}
          </Typography>
          {pageActions && (
            <Box sx={{ ml: 'auto' }}>
              {pageActions}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: 'width 0.2s ease-in-out',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          transition: 'width 0.2s ease-in-out',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};